import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWeb } from '../context/WebContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { FaChevronRight, FaCheckCircle, FaFilter } from 'react-icons/fa';

export default function Services() {
  const { services, settings } = useWeb();
  
  // Dynamic page SEO meta tags
  useDocumentMeta(
    `Our Premium Installation Services | ${settings.companyName}`,
    'Browse our comprehensive selection of safety net installations, invisible grills, sports boundary nets, and clothes hangers. Certified quality and fast setup.'
  );

  const categories = ['All', 'Bird Nets', 'Invisible Grills', 'Sports Nets', 'Cloth Hangers'];
  const [activeCategory, setActiveCategory] = useState('All');

  // Filter services by category
  const filteredServices = activeCategory === 'All' 
    ? services 
    : services.filter(s => s.category === activeCategory);

  return (
    <div className="bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumbs */}
        <nav className="flex space-x-2 items-center text-xs text-slate-500 mb-8 py-1">
          <Link to="/" className="hover:text-orange-600">Home</Link>
          <FaChevronRight className="text-[10px]" />
          <span className="text-slate-800 font-semibold">Services</span>
        </nav>

        {/* Heading */}
        <div className="text-center space-y-4 mb-12">
          <span className="text-xs uppercase bg-orange-50 text-orange-600 font-extrabold px-4 py-1.5 rounded-full border border-orange-200">
            Professional Installation Services
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
            Our Certified <span className="bg-gradient-to-r from-orange-600 to-amber-500 bg-clip-text text-transparent">Safety Net Solutions</span>
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm sm:text-base font-medium leading-relaxed">
            Select a category below to explore professional installations and request a free site measurement and quote.
          </p>
        </div>

        {/* Filters / Category Tabs */}
        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mb-10 border-b border-slate-200 pb-6">
          <div className="text-slate-400 mr-2 hidden md:flex items-center space-x-1.5 text-xs font-bold uppercase tracking-wider">
            <FaFilter />
            <span>Category:</span>
          </div>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                activeCategory === cat
                  ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/25'
                  : 'bg-white text-slate-650 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredServices.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-3xl overflow-hidden border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full group"
            >
              {/* Image Preview */}
              <div className="relative h-48 overflow-hidden bg-slate-100">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-orange-600/90 backdrop-blur-sm text-white font-bold text-[10px] sm:text-xs px-3 py-1 rounded-full border border-orange-400/25 shadow-md">
                  {service.category || 'General'}
                </div>
                <div className="absolute bottom-4 right-4 bg-slate-900/85 backdrop-blur-sm text-white font-extrabold text-[10px] px-2.5 py-1 rounded-lg">
                  {service.priceGuideline}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 flex flex-col flex-grow justify-between space-y-4">
                <div className="space-y-2">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-950 group-hover:text-orange-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-500 text-xs sm:text-sm leading-relaxed line-clamp-3">
                    {service.shortDesc}
                  </p>
                </div>

                {/* Benefits */}
                <div className="space-y-2 border-t pt-4">
                  {service.benefits && service.benefits.slice(0, 3).map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-xs text-slate-500">
                      <FaCheckCircle className="text-orange-500 text-[10px] flex-shrink-0" />
                      <span className="truncate">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* Card Action */}
                <div className="pt-2">
                  <Link
                    to={`/services/${service.id}`}
                    className="w-full flex items-center justify-center space-x-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl transition text-xs sm:text-sm cursor-pointer shadow-md"
                  >
                    <span>View Pricing & Details</span>
                    <FaChevronRight className="text-[10px]" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredServices.length === 0 && (
          <div className="text-center py-16 bg-white border rounded-3xl p-8 space-y-3">
            <p className="text-slate-400 font-bold text-base">No services found under this category.</p>
            <p className="text-xs text-slate-500">Please choose another filter or select 'All'.</p>
          </div>
        )}

      </div>
    </div>
  );
}
